import { createListenerMiddleware } from '@reduxjs/toolkit';
import { logger, monitoring } from 'common/services';
import { selectBackgroundImageId, userActions } from 'user/state';

// Imported from the module rather than the `../hooks` barrel: that barrel re-exports hooks which
// import from this folder, and closing the loop would be an initialisation-order bug in a prod build.
import { evictBackgroundImage } from '../hooks/useVideoBackground/utils/loadBackgroundImage';
import {
  getCustomBackgroundImageUrl,
  getUsedChars,
  invalidateCustomBackgroundUrls,
  readCustomBackgroundIndex,
  removeCustomBackground,
} from '../services';
import { customBackgroundDeleted, customBackgroundLimitReached } from './customBackgrounds.slice';

import type { RootState } from 'store';

export const customBackgroundsListener = createListenerMiddleware<RootState>();

/**
 * Someone wanted another background and the cap stopped them.
 *
 * This is the number that decides whether the 8-image cap — and with it the whole synchronous
 * localStorage design — is still the right trade. If it barely fires, the cap is costing nobody
 * anything. If it fires often, that's the case for moving the bytes to the Cache API and taking on
 * the async complexity that buys.
 */
customBackgroundsListener.startListening({
  actionCreator: customBackgroundLimitReached,
  effect: () => {
    const context = { count: readCustomBackgroundIndex().length, totalChars: getUsedChars() };

    logger.warn('[customBackgrounds] Refused a custom background: limitReached', context);
    monitoring.trackAction('devices.video_background.custom_limit_reached', context);
  },
});

customBackgroundsListener.startListening({
  actionCreator: customBackgroundDeleted,
  effect: ({ payload: id }, { getOriginalState, dispatch }) => {
    // Both caches are keyed by the data URL, so read it before the bytes go. Two layers to clear: the
    // string memo in `services`, and the decoded `HTMLImageElement` one layer up in
    // `loadBackgroundImage` — dropping only the first leaves the expensive half behind.
    const imageUrl = getCustomBackgroundImageUrl(id);

    removeCustomBackground(id);
    invalidateCustomBackgroundUrls(id);
    if (imageUrl) evictBackgroundImage(imageUrl);

    monitoring.trackAction('devices.video_background.custom_deleted', {
      count: readCustomBackgroundIndex().length,
    });

    if (selectBackgroundImageId(getOriginalState()) !== id) return;

    /**
     * The deleted image was the one in use, so the selection has to go too — everywhere, not just in
     * this slice. `videoBackgroundShed` is the existing lever that does all four at once: it clears
     * `userSlice`, `user/state/listener` clears `localStorage`, `useDevicesFormAutoUpdate` clears any
     * open settings form (and with it the preview), and `useVideoBackgroundForVideo` re-runs to clear
     * the published track. Without this the user would keep broadcasting an image they just deleted.
     */
    dispatch(userActions.mediaSettings.videoBackgroundShed());
  },
});
